import { useState, useEffect } from 'react';
import { X, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/hooks/useLanguage';
import { getConsentTranslation } from '@/i18n/consent-translations';
import { loadConsentState, ConsentPreferences } from '@/lib/tracking/consent-store';

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: ConsentPreferences) => void;
}

interface CategoryCardProps {
  title: string;
  description: string;
  details: string;
  isEnabled: boolean;
  isRequired?: boolean;
  onToggle: (enabled: boolean) => void;
}

const CategoryCard = ({
  title,
  description,
  details,
  isEnabled,
  isRequired = false,
  onToggle,
}: CategoryCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="p-4 bg-background">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-foreground">{title}</h4>
              {isRequired && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground">
                  <Lock className="h-3 w-3 mr-1" />
                  Required
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          
          <Switch
            checked={isEnabled}
            onCheckedChange={onToggle}
            disabled={isRequired}
            aria-label={`Toggle ${title}`}
          />
        </div>
        
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 mt-2 text-xs text-primary hover:text-primary/80"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              Hide details
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              Show details
            </>
          )}
        </button>
      </div>
      
      {isExpanded && (
        <div className="px-4 py-3 bg-muted/50 border-t border-border">
          <p className="text-xs text-muted-foreground">{details}</p>
        </div>
      )}
    </div>
  );
};

const PreferencesModal = ({ isOpen, onClose, onSave }: PreferencesModalProps) => {
  const { language } = useLanguage();
  const t = getConsentTranslation(language);
  
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    if (isOpen) {
      const state = loadConsentState();
      setPreferences(state.preferences);
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAcceptAll = () => {
    onSave({
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    });
  };

  const handleRejectAll = () => {
    onSave({
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    });
  };

  const handleSave = () => {
    onSave(preferences);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preferences-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-hidden bg-background rounded-xl shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-4 md:p-6 border-b border-border bg-background z-10">
          <h2 id="preferences-title" className="text-xl font-semibold text-foreground">
            {t.modal.title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <p className="text-sm text-muted-foreground mb-6">
            {t.modal.description}
          </p>
          
          <div className="space-y-4">
            <CategoryCard
              title={t.modal.categories.necessary.title}
              description={t.modal.categories.necessary.description}
              details={t.modal.categories.necessary.details}
              isEnabled={true}
              isRequired={true}
              onToggle={() => {}}
            />
            
            <CategoryCard
              title={t.modal.categories.analytics.title}
              description={t.modal.categories.analytics.description}
              details={t.modal.categories.analytics.details}
              isEnabled={preferences.analytics}
              onToggle={(enabled) => setPreferences(p => ({ ...p, analytics: enabled }))}
            />
            
            <CategoryCard
              title={t.modal.categories.marketing.title}
              description={t.modal.categories.marketing.description}
              details={t.modal.categories.marketing.details}
              isEnabled={preferences.marketing}
              onToggle={(enabled) => setPreferences(p => ({ ...p, marketing: enabled }))}
            />
            
            <CategoryCard
              title={t.modal.categories.personalization.title}
              description={t.modal.categories.personalization.description}
              details={t.modal.categories.personalization.details}
              isEnabled={preferences.personalization}
              onToggle={(enabled) => setPreferences(p => ({ ...p, personalization: enabled }))}
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 p-4 md:p-6 border-t border-border bg-background">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              onClick={handleSave}
              className="w-full sm:w-auto sm:flex-1"
            >
              {t.modal.savePreferences}
            </Button>
            
            <Button
              onClick={handleAcceptAll}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t.modal.acceptAll}
            </Button>
            
            <Button
              onClick={handleRejectAll}
              variant="ghost"
              className="w-full sm:w-auto"
            >
              {t.modal.rejectAll}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferencesModal;
